import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import {
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  GlobeAltIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

const Navbar = ({
  onOpenTimezoneModal,
  isSidebarCollapsed = false,
  className = "",
}) => {
  const { user, logout } = useAuth();
  const { headerContent } = usePageHeader();
  const navigate = useNavigate();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);

  const handleLogout = async () => {
    setPopoverOpen(false);
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleProfileClick = () => {
    setPopoverOpen(false);
    navigate("/dashboard/profile");
  };

  const handleTimezoneClick = () => {
    setPopoverOpen(false);
    onOpenTimezoneModal?.();
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header
      className={`fixed top-0 right-0 z-20 flex items-center backdrop-blur-lg justify-between gap-4 px-4 sm:px-6 lg:px-8 py-4 border-b border-gray-200 ${
        isSidebarCollapsed ? "lg:left-16" : "lg:left-72"
      } ${className}`}
    >
      <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        {headerContent}
      </div>

      {/* Avatar with popover */}
      <div className="relative shrink-0 ml-4">
        <button
          ref={buttonRef}
          onClick={() => setPopoverOpen(!popoverOpen)}
          className="flex items-center border border-gray-200 gap-2 p-1.5 rounded-full hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2"
          aria-expanded={popoverOpen}
          aria-haspopup="true"
        >
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-primary font-semibold text-sm">
              {user?.name?.[0]?.toUpperCase() || "👤"}
            </span>
          </div>
          {user?.name && (
            <span className="text-sm font-semibold">{user?.name}</span>
          )}
          <ChevronDownIcon
            className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
              popoverOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {popoverOpen && (
          <div
            ref={popoverRef}
            className="absolute right-0 mt-2 w-56 rounded-xl bg-white shadow-lg ring-1 ring-black/5 py-1.5 z-50"
          >
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="font-semibold text-gray-900 truncate">
                {user?.name || "User"}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
            <div className="py-1">
              <button
                onClick={handleProfileClick}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                <UserCircleIcon className="h-5 w-5 text-gray-400" />
                Profile
              </button>
              <button
                onClick={handleTimezoneClick}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                Timezone
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-danger transition-colors duration-200"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 text-gray-400" />
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
